import {
  closeWidget,
  getCurrentOpenWidgetIds,
  getCurrentThread,
} from '@/store/reducers/UIState';
import { EventType } from '@/models/Interaction';
import { fetchLatestDataVoyagerState } from '@/store/thunks/FetchDataVoyager';
import { fetchLatestPaperFinderState } from '@/store/thunks/FetchPaperFinder';
import { fetchLatestReportState } from '@/store/thunks/FetchReport';
import { fetchLatestTableState } from '@/store/thunks/FetchTable';
import { getActiveWidgetDataForThread } from '@/store/reducers/PaperFinderWidgetStore';
import { getChatAPI } from '@/api/ChatApi';
import { getDataVoyagerStateForThread } from '@/store/reducers/DataVoyagerWidgetStore';
import { getReaderStateForThread } from '@/store/reducers/ReaderWidgetStore';
import { getReportStateForThread } from '@/store/reducers/ReportWidgetStore';
import { getTableStateForThread } from '@/store/reducers/TableWidgetStore';
import {
  getWidgetTypefromId,
  openDataVoyagerWidget,
  openPaperFinderWidget,
  openReaderWidget,
  openReportWidget,
  openTableWidget,
} from '@/store/actionCreators/WidgetActions';
import {
  mkDataVoyagerBtnTrackAttrs,
  mkPaperFinderBtnTrackAttrs,
  mkReaderBtnTrackAttrs,
  mkReportBtnTrackAttrs,
  mkTableBtnTrackAttrs,
} from '@/analytics/props/widgetMenu';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { WidgetEvent, WidgetType } from '@/models/PanelWidgets';

import { Badge, Button, ButtonGroup, styled, Tooltip } from '@mui/material';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import ArticleIcon from '@mui/icons-material/Article';
import DatasetIcon from '@mui/icons-material/Dataset';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SearchIcon from '@mui/icons-material/Search';
import TableChartIcon from '@mui/icons-material/TableChart';

export const WidgetMenu = (): ReactNode => {
  const dispatch = useAppDispatch();
  const chatApi = getChatAPI();
  const currentThread = useAppSelector(getCurrentThread);
  const [alertTable, setAlertTable] = useState(false);
  const [tableIsDirty, setTableIsDirty] = useState(false);

  // fetch widget data from stores
  const tableData = useAppSelector((state) =>
    getTableStateForThread(state, currentThread?.id),
  );

  const reportData = useAppSelector((state) =>
    getReportStateForThread(state, currentThread?.id),
  );

  const readerData = useAppSelector((state) =>
    getReaderStateForThread(state, currentThread?.id),
  );

  const paperFinderData = useAppSelector((state) =>
    getActiveWidgetDataForThread(state, currentThread?.id),
  );

  const dataVoyagerData = useAppSelector((state) =>
    getDataVoyagerStateForThread(state, currentThread?.id),
  );

  const openWidgetIds = useAppSelector(getCurrentOpenWidgetIds);

  // fetch widget data if not already in store
  useEffect(() => {
    if (!tableData && currentThread) {
      dispatch(fetchLatestTableState(currentThread.id));
    }
  }, [currentThread, tableData]);

  useEffect(() => {
    if (!reportData && currentThread) {
      dispatch(fetchLatestReportState(currentThread.id));
    }
  }, [currentThread, reportData]);

  useEffect(() => {
    if (currentThread) {
      dispatch(fetchLatestPaperFinderState(currentThread.id));
    }
  }, [currentThread]);

  useEffect(() => {
    if (!dataVoyagerData && currentThread) {
      dispatch(fetchLatestDataVoyagerState(currentThread.id));
    }
  }, [currentThread, reportData]);

  const openTable = () => {
    if (!currentThread || !tableData) {
      // this should never really happen
      return;
    }
    dispatch(
      openTableWidget({
        currentThreadId: currentThread.id,
        tableData: tableData,
      }),
    );
  };

  const openReader = (): void => {
    if (!currentThread || !readerData) {
      return;
    }

    dispatch(
      openReaderWidget({
        currentThreadId: currentThread.id,
        corpusId: readerData.corpusId,
        highlights: readerData.highlights,
      }),
    );
  };

  const openPaperFinder = (): void => {
    if (!currentThread || !paperFinderData) {
      return;
    }

    dispatch(
      openPaperFinderWidget({
        threadId: currentThread.id,
        widgetId: paperFinderData.id,
      }),
    );
  };

  const openReport = (): void => {
    if (!currentThread) {
      return;
    }

    dispatch(
      openReportWidget({
        currentThreadId: currentThread?.id,
        reportData,
      }),
    );
  };

  const openDataVoyager = (): void => {
    if (!currentThread) {
      return;
    }

    dispatch(
      openDataVoyagerWidget({
        currentThreadId: currentThread?.id,
        dvData: dataVoyagerData,
      }),
    );
  };

  const closePanel = useCallback((): void => {
    dispatch(closeWidget());

    // this should never really happen, but for tracking events we need to make its linked to a thread
    if (!currentThread) {
      return;
    }

    chatApi.trackEvent({
      type: EventType.UI_INTERACTION,
      threadId: currentThread.id,
      data: {
        widget_type: getWidgetTypefromId(openWidgetIds[0]),
        event_type: WidgetEvent.WIDGET_CLOSE,
      },
    });
  }, [currentThread, openWidgetIds]);

  const openedWidgets = new Set(
    openWidgetIds.map((id) => getWidgetTypefromId(id)),
  );

  const isTableOpen = openedWidgets.has(WidgetType.TABLE);
  const isReaderOpen = openedWidgets.has(WidgetType.READER);
  const isPaperFinderOpen = openedWidgets.has(WidgetType.PAPER_FINDER);
  const isReportOpen = openedWidgets.has(WidgetType.REPORT);
  const isDataVoyagerOpen = openedWidgets.has(WidgetType.DATA_VOYAGER);

  useEffect(() => {
    setTableIsDirty(true);
  }, [tableData]);

  useEffect(() => {
    if (isTableOpen) {
      setTableIsDirty(false);
      setAlertTable(false);
    } else {
      if (tableIsDirty && tableData) {
        setAlertTable(true);
      }
    }
  }, [tableIsDirty, isTableOpen]);

  return (
    <Layout>
      <ButtonGroup
        orientation="vertical"
        aria-label="Vertical button group"
        sx={{ width: `48px` }}
      >
        {tableData && (
          <Badge
            color="secondary"
            overlap="circular"
            badgeContent={alertTable ? ' ' : 0}
            showZero={false}
          >
            <Tooltip title="Literature Review Table" arrow placement="left">
              <span>
                <Button
                  onClick={isTableOpen ? closePanel : openTable}
                  disabled={!currentThread}
                  variant={isTableOpen ? 'contained' : 'outlined'}
                  {...mkTableBtnTrackAttrs({
                    isOpen: isTableOpen,
                  })}
                >
                  <TableChartIcon />
                </Button>
              </span>
            </Tooltip>
          </Badge>
        )}

        {readerData && (
          <Tooltip title="PDF Reader" arrow placement="left">
            <span>
              <Button
                onClick={isReaderOpen ? closePanel : openReader}
                variant={isReaderOpen ? 'contained' : 'outlined'}
                {...mkReaderBtnTrackAttrs({
                  isOpen: isReaderOpen,
                })}
              >
                <PictureAsPdfIcon />
              </Button>
            </span>
          </Tooltip>
        )}
        {paperFinderData && (
          <Tooltip title="Search Results" arrow placement="left">
            <span>
              <Button
                onClick={isPaperFinderOpen ? closePanel : openPaperFinder}
                disabled={!currentThread}
                variant={isPaperFinderOpen ? 'contained' : 'outlined'}
                {...mkPaperFinderBtnTrackAttrs({
                  isOpen: isPaperFinderOpen,
                })}
              >
                <SearchIcon />
              </Button>
            </span>
          </Tooltip>
        )}
        {reportData && (
          <Tooltip title="Report" arrow placement="left">
            <span>
              <Button
                onClick={isReportOpen ? closePanel : openReport}
                disabled={!currentThread}
                variant={isReportOpen ? 'contained' : 'outlined'}
                {...mkReportBtnTrackAttrs({
                  isOpen: isReportOpen,
                })}
              >
                <ArticleIcon />
              </Button>
            </span>
          </Tooltip>
        )}
        {dataVoyagerData && (
          <Tooltip title="Data Analysis Logs" arrow placement="left">
            <span>
              <Button
                onClick={isDataVoyagerOpen ? closePanel : openDataVoyager}
                disabled={!currentThread}
                variant={isDataVoyagerOpen ? 'contained' : 'outlined'}
                {...mkDataVoyagerBtnTrackAttrs({
                  isOpen: isDataVoyagerOpen,
                })}
              >
                <DatasetIcon />
              </Button>
            </span>
          </Tooltip>
        )}
      </ButtonGroup>
    </Layout>
  );
};

const Layout = styled('div')`
  padding-bottom: ${({ theme }) => theme.spacing(3)};
  padding-left: ${({ theme }) => theme.spacing(1.5)};
  padding-right: ${({ theme }) => theme.spacing(3)};
`;
